import type { FormField } from '@shared/components/add-form/add-form.component';
import type { GoodsStockResVO } from '@app/api/stock-service';
import type {
  PurchaseReturnOrderDetailItem,
  AvailablePO,
} from './purchase-return-order.config';

// ---------------------------------------------------------------------------
// Item calculation
// ---------------------------------------------------------------------------

export function calculateReturnAmount(returnQty: number, price: number): number {
  return returnQty * (price || 0);
}

export function convertTaxAmountToRate(taxAmount: number): number {
  return taxAmount >= 1 ? taxAmount / 100 : taxAmount;
}

export function convertTaxRateToAmount(taxRate: number): number {
  return taxRate < 1 ? taxRate * 100 : taxRate;
}

// availableQuantity is computed by IMS (Stock Service) in purchase-return-order.service.ts's
// stock-loading methods - not re-derived here from raw stockNum/reservedNum.
export function calculateAvailableQuantity(stockData: {
  availableQuantity?: number;
} | null): number {
  return stockData?.availableQuantity ?? 0;
}

/** Max return qty: PO remaining quantity, capped by current stock when present. */
export function getMaxReturnQty(item: {
  qtyReceived?: number;
  qtyReturnedBefore?: number;
  qtyAvailable?: number;
}): number {
  const poRemaining = Math.max(
    0,
    (item.qtyReceived || 0) - (item.qtyReturnedBefore || 0),
  );
  const stock = item.qtyAvailable;

  if (poRemaining > 0) {
    if (stock != null && !Number.isNaN(Number(stock))) {
      return Math.min(poRemaining, Number(stock));
    }
    return poRemaining;
  }

  return stock ?? 0;
}

export function validateItemsForSave(
  items: PurchaseReturnOrderDetailItem[],
  hasPO: boolean
): { isValid: boolean; errorKey?: string; field?: string } {
  if (items.length === 0) {
    return {
      isValid: false,
      errorKey: 'purchase_return_order.messages.no_items_to_return',
    };
  }
  for (const item of items) {
    const returnQty = item.returnQty || 0;
    if (returnQty > getMaxReturnQty(item)) {
      return {
        isValid: false,
        errorKey: 'purchase_return_order.messages.invalid_return_quantities',
      };
    }
  }
  if (!hasPO) {
    for (const item of items) {
      if (!item.price || item.price <= 0) {
        return {
          isValid: false,
          errorKey: 'common.validation.required',
          field: 'purchase_return_order.add.table.columns.unit_price',
        };
      }
      if (
        item.taxAmount !== undefined &&
        item.taxAmount !== null &&
        (item.taxAmount < 0 || item.taxAmount > 100)
      ) {
        return {
          isValid: false,
          errorKey: 'common.validation.invalid',
          field: 'purchase_return_order.add.table.columns.tax_amount',
        };
      }
    }
  }
  return { isValid: true };
}

// ---------------------------------------------------------------------------
// PO helpers
// ---------------------------------------------------------------------------

export function deduplicatePOs(pos: AvailablePO[]): AvailablePO[] {
  const seen = new Map<string, AvailablePO>();
  pos.forEach((po) => {
    const warehouseKey = po.warehouseCode || `whId-${po.warehouseId}`;
    const key = `${po.id}-${warehouseKey}`;
    if (!seen.has(key)) {
      seen.set(key, po);
    } else {
      const existing = seen.get(key)!;
      if (
        po.warehouseId &&
        po.warehouseId > 0 &&
        (!existing?.warehouseId || existing.warehouseId === 0)
      ) {
        seen.set(key, po);
      }
    }
  });
  return Array.from(seen.values());
}

export function createWarehouseOptionsFromPOs(
  pos: AvailablePO[]
): Array<{ label: string; value: number; code: string; name: string }> {
  const warehouseMap = new Map<
    number,
    { id: number; code: string; name: string }
  >();
  pos.forEach((po) => {
    if (po.warehouseId && !warehouseMap.has(po.warehouseId)) {
      warehouseMap.set(po.warehouseId, {
        id: po.warehouseId,
        code: po.warehouseCode,
        name: po.warehouseName,
      });
    }
  });
  return Array.from(warehouseMap.values()).map((wh) => ({
    label: `${wh.code} - ${wh.name}`,
    value: wh.id,
    code: wh.code,
    name: wh.name,
  }));
}

// ---------------------------------------------------------------------------
// Form options
// ---------------------------------------------------------------------------

export function updateFormFieldOptions(
  formFields: FormField[],
  controlName: string,
  options: Array<{ label: string; value: unknown }>
): FormField[] {
  return formFields.map((field) => {
    if (field.controlName === controlName) {
      return { ...field, options };
    }
    return field;
  });
}

// ---------------------------------------------------------------------------
// Stock mapping
// ---------------------------------------------------------------------------

type StockMap = Map<
  string,
  Map<string, GoodsStockResVO & { availableQuantity?: number }>
>;

export function mapPOItemsToDetailItemsByWarehouse(
  poDetails: { items?: Array<Record<string, unknown>> },
  stockMap: StockMap
): Map<string, PurchaseReturnOrderDetailItem[]> {
  const items = poDetails.items || [];
  if (items.length === 0) return new Map();

  const itemsByWarehouse = new Map<string, Array<Record<string, unknown>>>();
  items.forEach((item: Record<string, unknown>) => {
    const warehouseCode = item['warehouseCode'] as string;
    if (warehouseCode) {
      if (!itemsByWarehouse.has(warehouseCode)) {
        itemsByWarehouse.set(warehouseCode, []);
      }
      itemsByWarehouse.get(warehouseCode)!.push(item);
    }
  });

  const detailItemsMap = new Map<string, PurchaseReturnOrderDetailItem[]>();
  itemsByWarehouse.forEach((warehouseItems, warehouseCode) => {
    const detailItems: PurchaseReturnOrderDetailItem[] = warehouseItems.map(
      (item: Record<string, unknown>, index: number) => {
        const skuCode = (item['skuCode'] as string) || '';
        const skuStockMap = stockMap.get(skuCode);
        const stockData = skuStockMap?.get(warehouseCode);
        const availableQuantity = calculateAvailableQuantity(stockData ?? null);
        const taxRate = (item['tax'] as number) ?? 0;
        const taxAmount = convertTaxRateToAmount(taxRate);
        const stockNum = (stockData as { stockNum?: number })?.stockNum || 0;

        return {
          id: `item-${warehouseCode}-${index}`,
          grnDetailId: (item['id'] as number) || 0,
          skuCode,
          skuName: (item['skuName'] as string) || '',
          vendorSkuCode: (item['vendorSkuCode'] as string) ?? '',
          vendorSkuName: (item['vendorSkuName'] as string) ?? '',
          spuCode: (item['spuCode'] as string) || '',
          spuName: (item['spuName'] as string) || '',
          uom: '',
          qtyReceived: (item['quantity'] as number) || 0,
          qtyReturnedBefore: 0,
          qtyAvailable: availableQuantity,
          currentStock: stockNum,
          returnQty: 0,
          price: (item['price'] as number) || 0,
          returnAmount: 0,
          taxAmount,
          reason: '',
        };
      }
    );
    detailItemsMap.set(warehouseCode, detailItems);
  });

  return detailItemsMap;
}
